import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { RiftboundSegmentedControl } from '../ui/segmented-control';
import type { CollectionCopyLanguage, CollectionLanguageScope } from '../../utils/collectionLanguage';

const LANGUAGE_ITEMS = [
  { value: 'all', label: 'All' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'ko', label: '한국어' },
] as const satisfies readonly { value: CollectionLanguageScope; label: string }[];

function savedLanguageName(language: CollectionCopyLanguage): string {
  if (language === 'zh') return 'a Chinese';
  if (language === 'ko') return 'a Korean';
  return 'an English';
}

function languageHint(value: CollectionLanguageScope, newCopyLanguage: CollectionCopyLanguage): string {
  if (value === 'en') return 'English copies. + saves an English card.';
  if (value === 'zh') return 'Chinese copies. + saves a Chinese card.';
  if (value === 'ko') return 'Korean copies. + saves a Korean card.';
  return `Every copy. + saves ${savedLanguageName(newCopyLanguage)} card.`;
}

/** Shown on the mobile row only while All is selected, where + writes a different language than the label. */
export function collectionLanguageAllHint(newCopyLanguage: CollectionCopyLanguage): string {
  return `+ saves ${savedLanguageName(newCopyLanguage)} card.`;
}

/** Which printed language this collection page is counting. */
export function CollectionLanguageControl({
  value,
  onChange,
  newCopyLanguage = 'en',
  variant = 'card',
}: {
  value: CollectionLanguageScope;
  onChange: (value: CollectionLanguageScope) => void;
  /** Language a new copy gets while All is showing every copy. */
  newCopyLanguage?: CollectionCopyLanguage;
  /** `card` is the desktop sidebar. `inline` is the mobile page row. */
  variant?: 'card' | 'inline';
}) {
  const control = (
    <RiftboundSegmentedControl
      value={value}
      items={LANGUAGE_ITEMS}
      onValueChange={onChange}
      w="full"
      aria-label="Card language"
    />
  );

  if (variant === 'inline') {
    return (
      <Box mb={2}>
        {control}
        {value === 'all' ? (
          <Text fontSize="xs" color="textMuted" mt={2} lineHeight="short">
            {collectionLanguageAllHint(newCopyLanguage)}
          </Text>
        ) : null}
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={3} p={4} mb={2} bg="bgElevated" borderRadius="lg">
      <Heading as="h2" size="sm">Language</Heading>
      <Box>
        {control}
        <Text fontSize="xs" color="textMuted" mt={2} lineHeight="short">
          Each language is printed on its own date, so those copies stay separate. A set page lists the dates. {languageHint(value, newCopyLanguage)}
        </Text>
      </Box>
    </VStack>
  );
}
